import { Box, Typography, Grid, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { GREEN_LOGO, WHITE, GREEN_MAIN } from "../../theme/colors";
import { useTranslationContext } from "../../hooks/translations/translationsProvider";
import { Trans } from "react-i18next";
import settings from "../../utilities/settings";
import { checkUserEnabled } from "../../helpers/authorizedUser";
import { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { useUserContext } from "../../hooks/auth/UserProvider";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { closeAccount, existPendingCloseAccount } from "../../hooks/actions/users";
import { useWaiter } from "../../hooks/waiter/WaiterProvider";
import { CloseAccountStatus } from "../../models/enums/CloseAccountStatus";

export const CloseAccount = () => {
    const {t} = useTranslationContext();
    const {user, auth, checkIsAuth} = useUserContext();
    const navigate = useNavigate();
    const [isEnabled, setIsEnabled] = useState(false);
    const {isForbidden} = useUserContext();
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [mainError, setMainError] = useState<string | null>(null);
    const [hasPendingRequest, setHasPendingRequest] = useState<boolean>(false);
    const [checkPendingError, setCheckPendingError] = useState<boolean>(false);
    const [checkCompleted, setCheckCompleted] = useState<boolean>(false);
    const {setWaiterView} = useWaiter();

    useEffect(() => {
        if(isForbidden){
            navigate('/');
        }
    }, [isForbidden]);

    useEffect(() => {
        if (auth.isLoading) return;
    
        // Se l'utente non è autenticato → fai partire il login con redirect alla pagina corrente
        if (!auth.isAuthenticated) {
            auth.signinRedirect({ state: window.location.pathname });
            return;
        }
        
        // Se tutto ok → abilita la vista
        setIsEnabled(true);
    
        // Esegui comunque il controllo auth
        checkIsAuth().finally();

        // Verifica se c'è una richiesta di chiusura pendente
        existPendingCloseAccount().then(response => {
            if (response.status === 200) {
                // Se la risposta HTTP è 200, usa la risposta
                setHasPendingRequest(response.data.existPendingCloseAccount);
                setCheckPendingError(false);
            } else {
                debugger;
                setHasPendingRequest(false);
                setCheckPendingError(true);
            }
            setCheckCompleted(true);
        }).catch(error => {
            console.error('Errore durante la verifica della richiesta pendente:', error);
            setHasPendingRequest(false);
            setCheckPendingError(true);
            setCheckCompleted(true);
        });
    
    }, [auth.isLoading, auth.isAuthenticated, auth.user, user, navigate]);

    const handleOpenConfirmDialog = () => {
        setMainError(null);
        setConfirmDialogOpen(true);
    };

    const handleCloseConfirmDialog = () => {
        setConfirmDialogOpen(false);
    };

    const handleConfirmCloseAccount = async () => {
        setConfirmDialogOpen(false);
        setMainError(null);
        setWaiterView(true); // avvio il waiter
        
        try {
            const response = await closeAccount();
            
            if (response.status === 200) {
                // Se la risposta HTTP è 200, controlla lo status nel body
                if (response.data.status === CloseAccountStatus.Request) {
                    setSuccessDialogOpen(true);
                    
                    // Redirect automatico dopo 15 secondi
                    setTimeout(() => {
                        //auth.signoutSilent();
                        navigate('/');
                    }, 15000);
                } else {
                    // Errore nel body della risposta (status diverso da Request)
                    setMainError(response.data.error || t("Errore durante la richiesta di chiusura account"));
                }
            } else {
                // Risposta HTTP diversa da 200 - errore
                setMainError(response.data.error || t("Errore durante la richiesta di chiusura account"));
            }
            
            setWaiterView(false); // termina il waiter
        } catch (error: any) {
            setWaiterView(false); // termina il waiter in caso di errore
            
            let errorMex: string = t("Errore durante la richiesta di chiusura account");
            try {
                // Estrai solo il messaggio, non tutto l'oggetto
                if (error?.response?.data) {
                    const data = error.response.data;
                    if (data?.message && typeof data.message === 'string') {
                        errorMex = data.message;
                    } else if (typeof data === 'string') {
                        errorMex = data;
                    } else if (data?.error && typeof data.error === 'string') {
                        errorMex = data.error;
                    }
                } else if (error?.message && typeof error.message === 'string') {
                    errorMex = error.message;
                } else if (typeof error === 'string') {
                    errorMex = error;
                }
            } catch (e) {
                // Mantieni il messaggio di default
            }
            console.error('Errore chiusura account:', error);
            console.error('Messaggio errore:', errorMex);
            setMainError(errorMex);
        }
    };
    
    return(
        !isForbidden ? <Box sx={{
            width:'100%', 
            display:'flex', 
            flexDirection:'column',
            color: GREEN_MAIN,
            fontFamily:'poppins'
          }}>
            <Grid container justifyContent='center' paddingBottom={5}>
                <Grid item xs={12} sm={9} md={6}>
                    <Typography
                        variant="h3"
                        component="h1"
                        gutterBottom
                        sx={{
                            mb: 0,
                            mt: 0,
                            fontWeight: "bold",
                        }}
                    >
                        {t("Chiudi profilo")}
                    </Typography>
                </Grid>
            </Grid>

            <Grid container justifyContent='center' paddingBottom={5}>
                <Grid item xs={12} sm={9} md={6}>
                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: '1em',
                            mb: 2,
                            textAlign:"justify",
                            display: "flex",
                            flexWrap: "nowrap"
                        }}
                    >
                        <Box sx={{ flex: 1 }}>
                            <Trans i18nKey='close-account-description' />
                        </Box>
                    </Typography>
                </Grid>
            </Grid>

            {hasPendingRequest && 
                <Grid container justifyContent='center' paddingBottom={3}>
                    <Grid item xs={12} sm={9} md={6}>
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: '1em',
                                mb: 2,
                                textAlign: "center",
                                color: GREEN_MAIN,
                                fontWeight: 'bold'
                            }}
                        >
                            {t("close-account-pending-message")}
                        </Typography>
                    </Grid>
                </Grid>
            }

            {checkPendingError && 
                <Grid container justifyContent='center' paddingBottom={3}>
                    <Grid item xs={12} sm={9} md={6}>
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: '1em',
                                mb: 2,
                                textAlign: "center",
                                color: GREEN_MAIN,
                                fontWeight: 'bold'
                            }}
                        >
                            {t("close-account-check-error-message")}
                        </Typography>
                    </Grid>
                </Grid>
            }

            <Grid container justifyContent='center' paddingBottom={5}>
                <Grid item xs={8} sm={5} md={3} lg={2}>
                    <Button 
                        variant="contained"
                        color="info"
                        onClick={handleOpenConfirmDialog}
                        disabled={!checkCompleted || hasPendingRequest || checkPendingError}
                        fullWidth
                        sx={{
                            margin: "10px 0 0 0",
                            height: '35px',
                            color: WHITE,
                            backgroundColor: GREEN_LOGO,
                            "&:hover": { backgroundColor: "darkgreen" },
                            border: "1px solid " + GREEN_LOGO,
                            textTransform: 'none',
                            "&:disabled": {
                                backgroundColor: `${GREEN_LOGO}50`,
                                color: WHITE,
                                border: `1px solid ${GREEN_LOGO}50`
                            }
                        }}
                    >
                        {t("Chiudi profilo")}
                    </Button>
                </Grid>
            </Grid>

            {mainError && 
                <Grid container justifyContent='center' paddingBottom={5}>
                    <Grid item xs={12} sm={9} md={6}>
                        <Typography
                            variant="body2" 
                            color="error"   
                            sx={{
                                maxHeight: '6.5em',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 5,
                                lineHeight: '1.3em',
                                wordWrap: 'break-word',
                                paddingTop: '20px'
                            }}
                        >
                            {String(mainError)}
                        </Typography>
                    </Grid>
                </Grid>
            }

            {/* Dialog di conferma */}
            <Dialog 
                open={confirmDialogOpen} 
                onClose={handleCloseConfirmDialog}
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        border: `1px solid ${GREEN_LOGO}30`,
                        minWidth: '400px'
                    }
                }}
            >
                <DialogTitle 
                    sx={{ 
                        color: GREEN_MAIN, 
                        fontWeight: 'bold',
                        fontSize: '1.4rem',
                        textAlign: 'center',
                        pb: 1
                    }}
                >
                    {t("Attenzione")}
                </DialogTitle>
                <DialogContent sx={{ px: 3, py: 2 }}>
                    <DialogContentText 
                        sx={{ 
                            color: GREEN_MAIN,
                            fontSize: '0.95rem',
                            textAlign: 'center',
                            lineHeight: 1.5
                        }}
                    >
                        {t("close-account-confirm")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 2 }}>
                    <Button 
                        onClick={handleCloseConfirmDialog}
                        variant="outlined"
                        sx={{
                            backgroundColor: 'white !important',
                            color: `${GREEN_MAIN} !important`,
                            borderColor: `${GREEN_MAIN} !important`,
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            '&:hover': {
                                backgroundColor: `${GREEN_LOGO}10 !important`,
                                borderColor: `${GREEN_LOGO} !important`,
                                color: `${GREEN_LOGO} !important`,
                            },
                            minWidth: '100px'
                        }}
                    >
                        {t("Annulla")}
                    </Button>
                    <Button 
                        onClick={handleConfirmCloseAccount}
                        sx={{
                            backgroundColor: GREEN_LOGO,
                            color: WHITE,
                            '&:hover': {
                                backgroundColor: GREEN_MAIN,
                            },
                            minWidth: '100px'
                        }}
                        autoFocus
                    >
                        {t("Chiudi profilo")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog di successo */}
            <Dialog 
                open={successDialogOpen} 
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        border: `1px solid ${GREEN_LOGO}30`,
                        minWidth: '400px'
                    }
                }}
            >
                <DialogTitle 
                    sx={{ 
                        color: GREEN_MAIN, 
                        fontWeight: 'bold',
                        fontSize: '1.4rem',
                        textAlign: 'center',
                        pb: 1
                    }}
                >
                    {t("Chiudi profilo")}
                </DialogTitle>
                <DialogContent sx={{ px: 3, py: 2 }}>
                    <DialogContentText 
                        sx={{ 
                            color: GREEN_MAIN,
                            fontSize: '0.95rem',
                            textAlign: 'center',
                            lineHeight: 1.5,
                            mb: 2
                        }}
                    >
                        {t("close-account-success")}
                    </DialogContentText>
                    <DialogContentText 
                        sx={{ 
                            color: GREEN_MAIN,
                            fontSize: '0.95rem',
                            textAlign: 'center',
                            lineHeight: 1.5
                        }}
                    >
                        {t("close-account-redirect")}
                    </DialogContentText>
                </DialogContent>
            </Dialog>
        </Box> : <></>
    )
}